import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Link2, Trash2, UserPlus } from 'lucide-react';
import { adminAtribuicaoService, adminUserService, catalogService } from '../../shared/services';

function mensagemDeErro(error: unknown, padrao: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string } | undefined;
    if (data?.message) return data.message;
  }
  return padrao;
}

function formatarData(valor?: string | null) {
  if (!valor) return '—';
  return new Date(valor).toLocaleDateString('pt-BR');
}

export function AdminAtribuicoesPage() {
  const queryClient = useQueryClient();
  const [trilhaId, setTrilhaId] = useState('');
  const [usuarioIds, setUsuarioIds] = useState<string[]>([]);
  const [prazoDias, setPrazoDias] = useState(30);
  const [obrigatoria, setObrigatoria] = useState(true);
  const [aviso, setAviso] = useState<string | null>(null);

  const { data: trilhasData } = useQuery({
    queryKey: ['trilhas-atribuicoes'],
    queryFn: () => catalogService.getTrilhas(),
  });
  const trilhas = Array.isArray(trilhasData) ? trilhasData : [];

  const { data: usuariosData } = useQuery({
    queryKey: ['usuarios-atribuicoes'],
    queryFn: () => adminUserService.getUsers(),
  });

  const usuarios = useMemo(
    () =>
      (Array.isArray(usuariosData) ? usuariosData : []).map((item: Record<string, unknown>) => ({
        id: String(item.id ?? item.Id ?? ''),
        nome: String(item.name ?? item.nome ?? item.Name ?? 'Sem nome'),
        email: String(item.email ?? item.Email ?? ''),
      })),
    [usuariosData],
  );

  const { data: atribuicoes = [] } = useQuery({
    queryKey: ['admin-atribuicoes', trilhaId],
    queryFn: () => adminAtribuicaoService.listar(trilhaId || undefined),
    enabled: !!trilhaId,
  });

  const atribuir = useMutation({
    mutationFn: () =>
      adminAtribuicaoService.atribuir({
        trilhaId,
        usuarioIds,
        prazoDias: prazoDias > 0 ? prazoDias : null,
        obrigatoria,
      }),
    onSuccess: (resultado) => {
      setAviso(`${resultado.criadas} atribuição(ões) criada(s); ${resultado.ignoradas} já existia(m).`);
      setUsuarioIds([]);
      queryClient.invalidateQueries({ queryKey: ['admin-atribuicoes'] });
    },
    onError: (error) => setAviso(mensagemDeErro(error, 'Não foi possível atribuir a trilha.')),
  });

  const remover = useMutation({
    mutationFn: (id: string) => adminAtribuicaoService.remover(id),
    onSuccess: () => {
      setAviso('Atribuição removida.');
      queryClient.invalidateQueries({ queryKey: ['admin-atribuicoes'] });
    },
    onError: (error) => setAviso(mensagemDeErro(error, 'Não foi possível remover a atribuição.')),
  });

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Link2 size={28} /> Atribuições de Trilhas
          </h1>
          <p className="text-gray-600 mt-1">
            Atribua uma trilha diretamente a colaboradores, com prazo e obrigatoriedade.
          </p>
        </div>

        {aviso && <div className="mb-4 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-800">{aviso}</div>}

        <form
          className="card mb-6 grid grid-cols-1 md:grid-cols-2 gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (!trilhaId || usuarioIds.length === 0) return;
            setAviso(null);
            atribuir.mutate();
          }}
        >
          <label className="block md:col-span-2">
            <span className="text-sm font-medium text-gray-700">Trilha</span>
            <select
              value={trilhaId}
              onChange={(event) => setTrilhaId(event.target.value)}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
              required
            >
              <option value="">Selecione a trilha</option>
              {trilhas.map((trilha) => (
                <option key={trilha.id} value={trilha.id}>{trilha.nome}</option>
              ))}
            </select>
          </label>

          <label className="block md:col-span-2">
            <span className="text-sm font-medium text-gray-700">
              Colaboradores <span className="text-xs text-gray-500">(use Ctrl/Cmd para selecionar vários)</span>
            </span>
            <select
              multiple
              value={usuarioIds}
              onChange={(event) => setUsuarioIds(Array.from(event.target.selectedOptions, (option) => option.value))}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 h-40"
              required
            >
              {usuarios.map((usuario) => (
                <option key={usuario.id} value={usuario.id}>
                  {usuario.nome} — {usuario.email}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="text-sm font-medium text-gray-700">Prazo (dias)</span>
            <input
              type="number"
              min={0}
              max={3650}
              value={prazoDias}
              onChange={(event) => setPrazoDias(Number(event.target.value))}
              className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2"
            />
          </label>

          <label className="flex items-center gap-2 mt-6">
            <input
              type="checkbox"
              checked={obrigatoria}
              onChange={(event) => setObrigatoria(event.target.checked)}
            />
            <span className="text-sm text-gray-700">Trilha obrigatória</span>
          </label>

          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={atribuir.isPending || !trilhaId || usuarioIds.length === 0}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              <UserPlus size={16} /> {atribuir.isPending ? 'Atribuindo...' : 'Atribuir trilha'}
            </button>
          </div>
        </form>

        {trilhaId && (
          <section className="card overflow-x-auto">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Atribuições desta trilha</h2>
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4">Colaborador</th>
                  <th className="py-2 pr-4">Origem</th>
                  <th className="py-2 pr-4">Prazo</th>
                  <th className="py-2 pr-4">Conclusão</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {atribuicoes.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-3 pr-4">
                      <p className="font-medium text-gray-900">{item.usuarioNome}</p>
                      <p className="text-xs text-gray-500">{item.usuarioEmail}</p>
                    </td>
                    <td className="py-3 pr-4 text-gray-600">{item.origem}</td>
                    <td className={`py-3 pr-4 ${item.atrasada ? 'text-red-700 font-medium' : 'text-gray-600'}`}>
                      {formatarData(item.prazoEm)}
                    </td>
                    <td className="py-3 pr-4 text-gray-600">{formatarData(item.concluidaEm)}</td>
                    <td className="py-3 pr-4">
                      {item.concluidaEm ? (
                        <span className="text-green-700 text-xs font-medium">Concluída</span>
                      ) : item.atrasada ? (
                        <span className="text-red-700 text-xs font-medium">Atrasada</span>
                      ) : (
                        <span className="text-gray-600 text-xs">Em andamento</span>
                      )}
                    </td>
                    <td className="py-3 text-right">
                      {!item.concluidaEm && (
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Remover a atribuição de ${item.usuarioNome}?`)) {
                              remover.mutate(item.id);
                            }
                          }}
                          className="text-gray-400 hover:text-red-600"
                          title="Remover atribuição"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {atribuicoes.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center text-gray-500">Nenhuma atribuição ativa.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
        )}
      </div>
    </div>
  );
}
